import { ORDER_SOURCES, type CarrierOption, type ProductListItem } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { decimalInput, fmt } from '../../format';
import { useSession } from '../../session';
import type { OrderSummary } from '../../types';
import { ErrorBanner, ProductThumb, Spinner, useLoad } from '../../ui';
import { DocumentPreview } from '../../ui/documents';
import { Icon } from '../../ui/icons';

interface Line {
  readonly product: ProductListItem;
  readonly qty: string;
  readonly price: string;
}

const EMPTY = { customerName: '', phone: '', email: '', company: '', line1: '', neighborhood: '', district: '', city: '', postalCode: '', orderNumber: '', note: '' };
const carrierKey = (c: CarrierOption) => `${c.code}|${c.shippingModel}`;

/**
 * A manual order (Instagram, WhatsApp, B2B, own web shop): the recipient, the products and the
 * carrier. Once saved the carrier registers the parcel and the label is ready for packing; the
 * seller gets the tracking number to pass on to the buyer.
 */
export function NewOrder() {
  const { warehouses } = useSession();
  const carriers = useLoad(() => api<CarrierOption[]>('GET', '/api/orders/carriers'), []);
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id ?? '');
  const [form, setForm] = useState(EMPTY);
  const [source, setSource] = useState<string>(ORDER_SOURCES[0]);
  const [lines, setLines] = useState<Line[]>([]);
  const [carrier, setCarrier] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<OrderSummary | null>(null);

  const usable = (carriers.data ?? []).filter((c) => c.available);
  // the default contract is preselected
  useEffect(() => {
    if (carrier === null && usable.length > 0) setCarrier(carrierKey(usable.find((c) => c.isDefault) ?? usable[0]!));
  }, [carriers.data]);
  const chosen = usable.find((c) => carrierKey(c) === carrier) ?? null;

  const term = search.trim();
  const found = useLoad(() => (term.length < 2 ? Promise.resolve([]) : api<ProductListItem[]>('GET', `/api/products?q=${encodeURIComponent(term)}&status=active`)), [term]);
  const set = (key: keyof typeof EMPTY) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const add = (product: ProductListItem) => {
    setLines((all) => (all.some((l) => l.product.id === product.id) ? all : [...all, { product, qty: '1', price: '' }]));
    setSearch('');
  };

  const phoneDigits = form.phone.replace(/\D/g, '').length;
  const problems = [
    !form.customerName.trim() && 'alıcı adı',
    phoneDigits < 10 && 'telefon',
    !form.line1.trim() && 'adres',
    !form.district.trim() && 'ilçe',
    !form.city.trim() && 'şehir',
    lines.length === 0 && 'en az bir ürün',
    lines.some((l) => !/^\d+$/.test(l.qty) || Number(l.qty) < 1) && 'geçerli adetler',
    lines.some((l) => l.price.trim() !== '' && decimalInput(l.price) === null) && 'geçerli fiyatlar',
    !chosen && 'kargo firması',
  ].filter((p): p is string => Boolean(p));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const order = await api<OrderSummary>('POST', '/api/orders', {
        warehouseId,
        orderNumber: form.orderNumber.trim() || null,
        customerName: form.customerName.trim(),
        customerEmail: form.email.trim() || null,
        customerPhone: form.phone.trim(),
        shippingAddress: {
          fullName: form.customerName.trim(),
          company: form.company.trim() || null,
          phone: form.phone.trim(),
          line1: form.line1.trim(),
          line2: null,
          neighborhood: form.neighborhood.trim() || null,
          district: form.district.trim(),
          city: form.city.trim(),
          postalCode: form.postalCode.trim() || null,
          countryCode: 'TR',
        },
        carrierCode: chosen!.code,
        shippingModel: chosen!.shippingModel,
        orderSource: source.trim() || null,
        sellerNote: form.note.trim() || null,
        lines: lines.map((l) => ({ sku: l.product.sku, qty: Number(l.qty), unitPrice: l.price.trim() ? decimalInput(l.price) : null })),
      });
      setCreated(order);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Created
        order={created}
        carrier={chosen}
        onAnother={() => {
          setCreated(null);
          setForm(EMPTY);
          setLines([]);
        }}
      />
    );
  }

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/siparisler">
          <Icon name="back" /> Siparişler
        </a>
        <a className="btn ghost" href="#/siparisler/ice-aktar">
          <Icon name="upload" /> Excel ile çok sipariş yükle
        </a>
      </div>
      <section className="card">
        <div className="card-h">
          <h2>Alıcı</h2>
          <span className="sub">Kargo etiketine bu bilgiler basılır</span>
        </div>
        <div className="card-b form-grid">
          <div className="field">
            <label htmlFor="o-name">Ad soyad</label>
            <input id="o-name" className="input" value={form.customerName} onChange={set('customerName')} autoComplete="off" />
          </div>
          <div className="field">
            <label htmlFor="o-phone">Telefon</label>
            <input id="o-phone" className="input" inputMode="tel" value={form.phone} onChange={set('phone')} placeholder="0555 123 45 67" />
            {form.phone && phoneDigits < 10 && <span className="hint bad">Kargo firması alıcı telefonunu ister</span>}
          </div>
          <div className="field">
            <label htmlFor="o-email">E-posta (isteğe bağlı)</label>
            <input id="o-email" className="input" type="email" value={form.email} onChange={set('email')} />
          </div>
          <div className="field">
            <label htmlFor="o-company">Firma (B2B, isteğe bağlı)</label>
            <input id="o-company" className="input" value={form.company} onChange={set('company')} />
          </div>
          <div className="field wide">
            <label htmlFor="o-line1">Adres</label>
            <input id="o-line1" className="input" value={form.line1} onChange={set('line1')} placeholder="Cadde, sokak, bina no, daire" />
          </div>
          <div className="field">
            <label htmlFor="o-neigh">Mahalle</label>
            <input id="o-neigh" className="input" value={form.neighborhood} onChange={set('neighborhood')} />
          </div>
          <div className="field">
            <label htmlFor="o-district">İlçe</label>
            <input id="o-district" className="input" value={form.district} onChange={set('district')} />
          </div>
          <div className="field">
            <label htmlFor="o-city">Şehir</label>
            <input id="o-city" className="input" value={form.city} onChange={set('city')} />
          </div>
          <div className="field">
            <label htmlFor="o-postal">Posta kodu</label>
            <input id="o-postal" className="input" inputMode="numeric" value={form.postalCode} onChange={set('postalCode')} />
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Ürünler</h2>
          <span className="sub">{lines.reduce((s, l) => s + (Number(l.qty) || 0), 0)} adet</span>
        </div>
        <div className="card-b stack">
          <div className="search-box">
            <Icon name="search" />
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün adı, SKU veya barkod ile arayın" aria-label="Ürün ara" />
          </div>
          {term.length >= 2 && found.data && (
            <ul className="hit-list">
              {found.data.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <button className="hit-row" onClick={() => add(p)}>
                    <ProductThumb name={p.name} imageUrl={p.imageUrl} small />
                    <b>{p.name}</b> <span className="mono muted">{p.sku}</span>
                    <span className={`chip ${p.available > 0 ? 'ok' : 'warn'}`}>{p.available > 0 ? `${fmt.n(p.available)} kullanılabilir` : 'stok yok'}</span>
                  </button>
                </li>
              ))}
              {found.data.length === 0 && <li className="muted">Bu aramaya uyan ürün yok.</li>}
            </ul>
          )}
          {lines.length === 0 ? (
            <div className="muted">Sipariş edilen ürünleri arayıp ekleyin.</div>
          ) : (
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th className="num">Stok</th>
                    <th className="num">Adet</th>
                    <th className="num">Birim fiyat (₺)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.product.id}>
                      <td>
                        <div className="product compact">
                          <ProductThumb name={l.product.name} imageUrl={l.product.imageUrl} small />
                          <div>
                            <div className="name">{l.product.name}</div>
                            <span className="mono muted">{l.product.sku}</span>
                          </div>
                        </div>
                      </td>
                      <td className="num">
                        {fmt.n(l.product.available)}
                        {Number(l.qty) > l.product.available && <div className="hint bad">stok gelince gönderilir</div>}
                      </td>
                      <td className="num">
                        <input
                          className="input"
                          inputMode="numeric"
                          style={{ width: 80, textAlign: 'right' }}
                          aria-label={`${l.product.sku} adet`}
                          value={l.qty}
                          onChange={(e) => setLines((all) => all.map((x) => (x.product.id === l.product.id ? { ...x, qty: e.target.value } : x)))}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="input"
                          inputMode="decimal"
                          style={{ width: 110, textAlign: 'right' }}
                          aria-label={`${l.product.sku} birim fiyat`}
                          placeholder="isteğe bağlı"
                          value={l.price}
                          onChange={(e) => setLines((all) => all.map((x) => (x.product.id === l.product.id ? { ...x, price: e.target.value } : x)))}
                        />
                      </td>
                      <td>
                        <button className="icon-btn" aria-label="Çıkar" onClick={() => setLines((all) => all.filter((x) => x.product.id !== l.product.id))}>
                          <Icon name="trash" size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Kargo ve sipariş</h2>
        </div>
        <div className="card-b stack">
          {carriers.error && <ErrorBanner message={carriers.error} onRetry={() => void carriers.reload()} />}
          {!carriers.data ? (
            <Spinner />
          ) : usable.length === 0 ? (
            <div className="banner warn">Tanımlı kargo sözleşmesi yok; depo yöneticisiyle görüşün.</div>
          ) : (
            <div className="choice-list">
              {usable.map((c) => (
                <label key={carrierKey(c)} className={`choice${carrier === carrierKey(c) ? ' on' : ''}`}>
                  <input type="radio" name="carrier" checked={carrier === carrierKey(c)} onChange={() => setCarrier(carrierKey(c))} />
                  <span>
                    <b>
                      {c.name} {c.sandbox && <span className="chip warn">Test ortamı</span>}
                    </b>
                    <small>
                      {c.shippingModel === 'warehouse_contract' ? 'Deponun sözleşmesi, kargo ücreti barem tablosundan' : 'Kendi sözleşmeniz'}
                      {c.integration === 'api' ? ' · gönderi kargo firmasına anında kaydedilir' : ' · takip numarası sözleşme aralığından'}
                    </small>
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="form-grid">
            {warehouses.length > 1 && (
              <div className="field">
                <label htmlFor="o-wh">Gönderen depo</label>
                <select id="o-wh" className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label htmlFor="o-source">Satış kaynağı</label>
              <input id="o-source" className="input" list="o-sources" value={source} onChange={(e) => setSource(e.target.value)} />
              <datalist id="o-sources">
                {ORDER_SOURCES.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
            <div className="field">
              <label htmlFor="o-number">Sipariş no (isteğe bağlı)</label>
              <input id="o-number" className="input mono" value={form.orderNumber} onChange={set('orderNumber')} placeholder="Boşsa otomatik verilir" />
            </div>
            <div className="field wide">
              <label htmlFor="o-note">Depoya not</label>
              <input id="o-note" className="input" value={form.note} onChange={set('note')} placeholder="Örn. hediye paketi" />
            </div>
          </div>
          {error && <div className="banner bad">{error}</div>}
          <div className="row">
            <button className="btn primary big" disabled={busy || problems.length > 0} onClick={() => void submit()}>
              <Icon name="check" /> Siparişi oluştur
            </button>
            {problems.length > 0 && <span className="hint">Eksik: {problems.join(', ')}</span>}
          </div>
        </div>
      </section>
    </>
  );
}

/** The order as the detail endpoint returns it, with its documents' errors. */
interface LiveOrder {
  readonly orderNumber: string;
  readonly carrierCode: string | null;
  readonly trackingNumber: string | null;
  readonly documents: readonly { readonly kind: string; readonly status: string; readonly error: string | null }[];
}

/** After saving: the tracking number as soon as the carrier gave one, and the label. */
function Created({ order, carrier, onAnother }: { order: OrderSummary; carrier: CarrierOption | null; onAnother: () => void }) {
  const live = useLoad(() => api<LiveOrder>('GET', `/api/orders/${order.id}`), [order.id], 2_000);
  const [preview, setPreview] = useState(false);
  const current = live.data ?? order;
  const label = live.data?.documents.find((d) => d.kind === 'shipping_label');
  return (
    <section className="card">
      <div className="card-b stack">
        <div className="banner ok">
          <Icon name="check" />
          <div>
            Sipariş <b className="mono">{current.orderNumber}</b> oluşturuldu; stok ayrılıp toplamaya alınacak.
          </div>
        </div>
        <dl className="summary">
          <dt>Kargo</dt>
          <dd>
            {carrier?.name ?? current.carrierCode ?? '—'} {carrier?.sandbox && <span className="chip warn">Test ortamı</span>}
          </dd>
          <dt>Takip no</dt>
          <dd>
            {current.trackingNumber ? (
              <span className="big-code">{current.trackingNumber}</span>
            ) : label?.status === 'failed' ? (
              <span className="hint bad">Etiket alınamadı: {label.error}</span>
            ) : (
              <span className="muted">Kargo firmasına kaydediliyor…</span>
            )}
          </dd>
        </dl>
        {carrier?.sandbox && current.trackingNumber && (
          <div className="banner warn">Test ortamı: bu takip numarası ve etiket Aras Kargo'ya gönderilmedi, kargo şubesinde geçmez.</div>
        )}
        <div className="row">
          {label?.status === 'ready' && (
            <button className="btn" onClick={() => setPreview(true)}>
              <Icon name="eye" /> Kargo etiketini gör
            </button>
          )}
          <button className="btn primary" onClick={onAnother}>
            <Icon name="plus" /> Yeni sipariş
          </button>
          <a className="btn ghost" href="#/siparisler">
            Siparişlere dön
          </a>
        </div>
      </div>
      {preview && <DocumentPreview orderId={order.id} orderNumber={current.orderNumber} kind="shipping_label" onClose={() => setPreview(false)} />}
    </section>
  );
}
